import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import Container from '@material-ui/core/Container';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import {Alert} from '@material-ui/lab';
import {Animated} from 'react-animated-css';

import PageHeaderAtm from '../../../atomic/PageHeader/PageHeader';
import {PageTitle} from '../../../atomic/PageHeader/PageTitle';
import {WEB, NETWORK_INVENTORY_SHORT} from '../../../../constants/appUrls.constants';
import {isDefined} from '../../../../utils';

import {getWeb} from '../../../../redux/actions/webActions';
import {publishWebSubmit} from '../../../../redux/actions/Web/PublishWeb/publishWebActions';
import {getEditContentWeb} from '../../../../redux/actions/Web/EditWeb/getEditContentWebActions';
import {getProfile} from '../../../../redux/actions/User/Profile/profileAction';
import {getBlogList} from '../../../../redux/actions/Web/Blog/blogActions';
import {getPopupList} from '../../../../redux/actions/Web/Popup/PopupActions';
import {getFaqList} from '../../../../redux/actions/Web/Faq/faqActions';
import {getGoogleRating} from '../../../../redux/actions/General/GoogleRating/googleRatingActions';
import {
    refreshWebMap,
    refreshWebReviews,
    clearWebComponentRefresh,
} from '../../../../redux/actions/Web/ComponentRefresh/webComponentRefreshActions';
import {
    getWebSettings,
    updateWebSettings,
    clearWebSettingsUpdate,
} from '../../../../redux/actions/Web/Settings/webSettingsActions';
import {WEB_SETTING_KEYS} from '../../../../constants/Web/webSettings.constants';
import {getPropertyInventory} from '../../../../redux/actions/Network/Inventory/inventoryAction';
import {buildInventoryGaps} from './inventoryGaps';

import settingsStyles from './settingsStyles';
import {isWebSettingsAllowed} from './allowedEmails';
import {SEARCH_ENGINE_SUBMISSION_ENABLED} from './featureFlags';
import useThirdPartyComponents from './useThirdPartyComponents';
import HealthScoreCard from './components/HealthScoreCard';
import PublishCard from './components/PublishCard';
import SearchEngineCard from './components/SearchEngineCard';
import SiteComponentsCard from './components/SiteComponentsCard';
import ThemeCard from './components/ThemeCard';
import InventoryGapsCard from './components/InventoryGapsCard';
import ImportCard from './components/ImportCard';
import GroupPageCard from './components/GroupPageCard';

// Sol menudeki bolumler — id'ler karttaki id ile birebir ayni olmali.
const SECTIONS = [
    {id: 'durum', icon: 'fas fa-tachometer-alt', label: 'enw-web-settings-nav_health', fallback: 'Site Durumu'},
    {id: 'yayin', icon: 'fas fa-globe', label: 'enw-web-settings-nav_publish', fallback: 'Yayın'},
    SEARCH_ENGINE_SUBMISSION_ENABLED
        ? {id: 'arama', icon: 'fas fa-search', label: 'enw-web-settings-nav_seo', fallback: 'Arama ve Ölçümleme'}
        : {id: 'arama', icon: 'fas fa-chart-line', label: 'enw-web-settings-nav_tracking', fallback: 'Ölçümleme'},
    {id: 'bilesenler', icon: 'fas fa-th-large', label: 'enw-web-settings-nav_components', fallback: 'Site Bileşenleri'},
    {id: 'tema', icon: 'fas fa-palette', label: 'enw-web-settings-nav_theme', fallback: 'Tema'},
    {id: 'kontenjan', icon: 'fas fa-calendar-times', label: 'enw-web-settings-nav_inventory', fallback: 'Kontenjan'},
    {id: 'aktarim', icon: 'fas fa-cloud-download-alt', label: 'enw-web-settings-nav_import', fallback: 'İçe Aktarım'},
    {id: 'sayfalar', icon: 'fas fa-layer-group', label: 'enw-web-settings-nav_pages', fallback: 'Sayfalar'},
];

// Siteden gelen rezervasyonlar Booking Engine kanalindan gectigi icin kontenjan
// bosluklari bu kanala bakilarak hesaplanir (Kontenjan ekrani da /inventory/1 adresini kullanir).
const BOOKING_ENGINE_CHANNEL_ID = 1;

// Kontenjan analizinin kapsadigi gun sayisi
const INVENTORY_DAY_RANGE = 30;

// Bugunden itibaren gun ekleyip YYYY-MM-DD dondurur
const shiftDate = (days) => moment().add(days, 'days').format('YYYY-MM-DD');

// Hata mesajini iki bicimden de cikarir:
//  - axios hatasi (REJECTED)            -> err.response.data.message
//  - HTTP 200 ama zarfta error: true    -> err.message
// Servis mesajlari `language` basligina gore cevriliyor ve son kullaniciya gosterilebilir.
const errorMessage = (err, fallback) => (
    (err && err.response && err.response.data && err.response.data.message)
    || (err && typeof err.message === 'string' && err.message)
    || fallback
);

// Liste servisleri kimi yerde diziyi, kimi yerde {data: [...]} zarfini donuyor.
const listCount = (listData) => {
    if (Array.isArray(listData)) return listData.length;
    if (listData && Array.isArray(listData.data)) return listData.data.length;
    return 0;
};

export default function WebSettings() {
    const {t} = useTranslation();
    const classes = settingsStyles();
    const dispatch = useDispatch();

    const webReducer = useSelector((state) => state.webReducer);
    const editContentReducer = useSelector((state) => state.getEditContentWebReducer);
    const profileReducer = useSelector((state) => state.profileReducer);
    const blogListData = useSelector((state) => state.blogReducers?.listData);
    const popupListData = useSelector((state) => state.popupReducers?.listData);
    const faqListData = useSelector((state) => state.faqReducers?.listData);

    // Web bileşenleri (Analytics, Tag Manager, Meta Pixel, Google Review…) ayrı ekranda
    // yönetiliyor; burada yalnızca aktif olanların özeti gösterilip o ekrana yönlendiriliyor.
    const thirdParty = useThirdPartyComponents();

    // Google puanı: property/google-rating/get hem kayıtlı puanı okur hem saatte bir
    // yorumları Google'dan tazeler (cron:google-review'ü on-demand tetikler).
    const googleRating = useSelector((state) => state.googleRatingReducer?.data?.data);
    const googleRatingFetching = useSelector((state) => state.googleRatingReducer?.fetching);

    // Google Review bileşeni ayarları — yorumların sitede görünme koşulu
    const googleReviewParams = thirdParty.googleReview
        && thirdParty.googleReview.componentDetail
        && thirdParty.googleReview.componentDetail.parameterArray;
    const reviewPlaceId = googleReviewParams && googleReviewParams.placeId;
    const reviewShowRating = !!(googleReviewParams && googleReviewParams.showRating);

    // Harita / yorum yenileme işlemleri
    const refreshState = useSelector((state) => state.webComponentRefreshReducer);

    // Kontenjan boşlukları — Kontenjan ekranıyla aynı uç, Booking Engine kanalı
    const inventoryData = useSelector((state) => state.inventoryReducer?.inventory);
    const inventoryFetching = useSelector((state) => state.inventoryReducer?.fetching);
    const inventoryGaps = useMemo(() => buildInventoryGaps(inventoryData), [inventoryData]);
    const inventoryHasData = Array.isArray(inventoryData) && inventoryData.length > 0;

    // Anahtar/değer tabanlı site ayarları (WhatsApp vb.)
    const storedSettings = useSelector((state) => state.webSettingsReducer?.settings);
    const settingsSaving = useSelector((state) => state.webSettingsReducer?.updateFetching);
    const settingsUpdateData = useSelector((state) => state.webSettingsReducer?.updateData);
    const settingsUpdateError = useSelector((state) => state.webSettingsReducer?.updateError);

    const [isPublish, setIsPublish] = useState(1);
    const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
    const [snackbar, setSnackbar] = useState(null);
    // Yenileme yanıtından gelen taze Google puanı (varsa kayıtlı değerin yerine gösterilir)
    const [freshRating, setFreshRating] = useState(null);

    // WhatsApp butonu henüz servise bağlanmadığı için değerler yerelde tutuluyor.
    // Harita ve yorumlar panelden ayarlanmıyor: harita konum bilgisinden otomatik üretilir,
    // yorumlar Web Bileşenleri'ndeki Google Review bileşeninden yönetilir.
    const [componentValues, setComponentValues] = useState({
        whatsappEnabled: false,
        whatsappNumber: '',
        phoneButtonEnabled: false,
        phoneButtonNumber: '',
        tripleButtonsEnabled: false,
        tripleWhatsappEnabled: false,
        tripleWhatsappNumber: '',
        tripleDirectionEnabled: false,
        triplePhoneEnabled: false,
        triplePhoneNumber: '',
    });

    // Yerel alan adi -> servis anahtari. Toggle'lar aninda, numaralar kendi
    // "Kaydet" butonlariyla gonderilir; ikisi de ayni haritalar uzerinden yurur.
    const TOGGLE_KEYS = {
        whatsappEnabled: WEB_SETTING_KEYS.WHATSAPP_ENABLED,
        phoneButtonEnabled: WEB_SETTING_KEYS.PHONE_BUTTON_ENABLED,
        tripleButtonsEnabled: WEB_SETTING_KEYS.TRIPLE_BUTTONS_ENABLED,
        tripleWhatsappEnabled: WEB_SETTING_KEYS.TRIPLE_WHATSAPP_ENABLED,
        tripleDirectionEnabled: WEB_SETTING_KEYS.TRIPLE_DIRECTION_ENABLED,
        triplePhoneEnabled: WEB_SETTING_KEYS.TRIPLE_PHONE_ENABLED,
    };

    const NUMBER_KEYS = {
        whatsappNumber: WEB_SETTING_KEYS.WHATSAPP_PHONE,
        phoneButtonNumber: WEB_SETTING_KEYS.PHONE_BUTTON_NUMBER,
        tripleWhatsappNumber: WEB_SETTING_KEYS.TRIPLE_WHATSAPP_NUMBER,
        triplePhoneNumber: WEB_SETTING_KEYS.TRIPLE_PHONE_NUMBER,
    };

    useEffect(() => {
        document.body.style.backgroundColor = '#f4f7fc';
        dispatch(getProfile());
        dispatch(getWeb());
        dispatch(getWebSettings());
        // bookingImportCheck burada çağrılmaz: BookingInfoImport bileşeni mount olurken
        // kendisi çağırıyor, buradan da tetiklenirse aynı uca iki istek gidiyordu.
        // Doğruluk oranı için içerik sayıları (blog / popup property_id ile çalışır)
        dispatch(getBlogList());
        dispatch(getPopupList());
        // Kontenjan boşlukları: bugünden itibaren INVENTORY_DAY_RANGE günlük pencere
        dispatch(getPropertyInventory(
            shiftDate(0),
            shiftDate(INVENTORY_DAY_RANGE),
            BOOKING_ENGINE_CHANNEL_ID
        ));
    }, [dispatch]);

    const domain = isDefined(webReducer) && isDefined(webReducer.domains) ? webReducer.domains : null;
    const webId = domain && domain.id;

    useEffect(() => {
        if (webId) {
            dispatch(getEditContentWeb({property_web_id: webId}));
            dispatch(getFaqList({property_web_id: webId}));
        }
    }, [dispatch, webId]);

    // Puan yalnızca Google Review bileşeni tanımlıysa anlamlı; servis tarafında
    // saatlik throttle olduğu için sayfa açılışında bir kez çağrılması yeterli.
    useEffect(() => {
        if (reviewPlaceId) {
            dispatch(getGoogleRating());
        }
    }, [dispatch, reviewPlaceId]);

    // Sol menude okunan bolumu isaretle. Kartlar web verisi geldikten sonra
    // basildigi icin gozlemci webId hazir oldugunda kurulur.
    useEffect(() => {
        if (!webId) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            {rootMargin: '-15% 0px -70% 0px'}
        );

        SECTIONS.forEach((section) => {
            const node = document.getElementById(section.id);
            if (node) observer.observe(node);
        });

        return () => observer.disconnect();
    }, [webId]);

    const handleNavClick = (id) => (e) => {
        e.preventDefault();
        const node = document.getElementById(id);
        if (node) {
            node.scrollIntoView({behavior: 'smooth', block: 'start'});
            setActiveSection(id);
        }
    };

    const handlePublish = (id, publish) => (e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        setIsPublish(publish);
        dispatch(publishWebSubmit({is_publish: publish}, id));
    };

    const handlePublishSuccessClose = () => {
        dispatch(getWeb());
    };

    // Toggle'lar anında kaydedilir; numaralar kendi "Kaydet" butonlarıyla gider.
    const handleComponentChange = (key, value) => {
        setComponentValues((prev) => ({...prev, [key]: value}));

        const settingKey = TOGGLE_KEYS[key];
        if (settingKey) {
            dispatch(updateWebSettings({[settingKey]: value ? 1 : 0}));
        }
    };

    // Girilen numara kayıtlı olandan farklıysa o alanın "Kaydet" butonu aktifleşir.
    // Boşaltmak da meşru bir değişiklik (otel numarayı kaldırabilir).
    const storedNumber = (field) => (storedSettings
        ? String(storedSettings[NUMBER_KEYS[field]] || '')
        : '');
    const typedNumber = (field) => String(componentValues[field] || '').trim();

    const numberDirty = Object.keys(NUMBER_KEYS).reduce((acc, field) => {
        acc[field] = typedNumber(field) !== storedNumber(field);
        return acc;
    }, {});

    const handleSaveNumber = (field) => () => {
        if (!numberDirty[field]) return;
        dispatch(updateWebSettings({[NUMBER_KEYS[field]]: typedNumber(field)}));
    };

    // Servisten gelen ayarları forma yansıt (sayfa açılışı ve kayıt sonrası)
    useEffect(() => {
        if (!storedSettings) return;
        setComponentValues((prev) => {
            const next = {...prev};
            Object.keys(TOGGLE_KEYS).forEach((field) => {
                next[field] = Number(storedSettings[TOGGLE_KEYS[field]]) === 1;
            });
            Object.keys(NUMBER_KEYS).forEach((field) => {
                next[field] = storedSettings[NUMBER_KEYS[field]] || '';
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedSettings]);

    // Ayar kaydının sonucunu bildir
    useEffect(() => {
        if (settingsUpdateData) {
            setSnackbar({
                severity: 'success',
                message: t('enw-web-settings-saved', 'Ayar kaydedildi.'),
            });
            dispatch(clearWebSettingsUpdate());
        } else if (settingsUpdateError) {
            setSnackbar({
                severity: 'error',
                message: errorMessage(
                    settingsUpdateError,
                    t('enw-web-settings-save_failed', 'Ayar kaydedilemedi. Lütfen tekrar deneyin.')
                ),
            });
            dispatch(clearWebSettingsUpdate());
        }
    }, [settingsUpdateData, settingsUpdateError, dispatch, t]);

    // Yenileme işlemlerinin sonucunu bildir; hata mesajı servisten geldiği gibi gösterilir.
    const handleRefreshMap = () => {
        dispatch(refreshWebMap());
    };

    const handleRefreshReviews = () => {
        dispatch(refreshWebReviews());
    };

    useEffect(() => {
        if (!refreshState) return;

        if (refreshState.mapData) {
            setSnackbar({
                severity: 'success',
                message: t('enw-web-settings-map_refreshed', 'Harita görseli yenilendi.'),
            });
            dispatch(clearWebComponentRefresh());
        } else if (refreshState.reviewData) {
            // data.results.review.data = {added, total, google_rating, google_rating_total}
            const {added, google_rating: rating, google_rating_total: ratingTotal} = refreshState.reviewData;
            const addedCount = Number(added) || 0;

            setSnackbar({
                severity: 'success',
                message: addedCount > 0
                    ? t('enw-web-settings-reviews_refreshed_count', {
                        defaultValue: 'Yorumlar yenilendi · {{count}} yeni yorum alındı.',
                        count: addedCount,
                    })
                    : t('enw-web-settings-reviews_refreshed', 'Yorumlar Google’dan yeniden alındı.'),
            });

            // Güncel puan yanıtın içinde geliyor; ayrıca google-rating/get çağırmaya gerek yok.
            if (rating !== null && rating !== undefined) {
                setFreshRating({google_rating: rating, google_rating_total: ratingTotal});
            }
            dispatch(clearWebComponentRefresh());
        } else if (refreshState.mapError || refreshState.reviewError) {
            const error = refreshState.mapError || refreshState.reviewError;
            setSnackbar({
                severity: 'error',
                message: errorMessage(
                    error,
                    t('enw-web-settings-refresh_failed', 'İşlem tamamlanamadı. Lütfen tekrar deneyin.')
                ),
            });
            dispatch(clearWebComponentRefresh());
        }
    }, [refreshState, dispatch, t]);

    // Servis ucu hazir olmayan islemler icin gecici bilgilendirme
    const notReady = useCallback(() => {
        setSnackbar({
            severity: 'info',
            message: t('enw-web-settings-not_ready', 'Bu özellik için servis bağlantısı henüz hazır değil.'),
        });
    }, [t]);

    const fetching = (webReducer && webReducer.fetching) || (editContentReducer && editContentReducer.fetching);
    const hasWeb = domain && domain.id;

    // Kademeli açılış: buton gizli olsa da adres çubuğundan girilebildiği için sayfa da
    // aynı listeyle korunur. E-posta gelmeden karar verilmez (o sırada yükleme görünür).
    const userEmail = profileReducer && profileReducer.profileData
        && profileReducer.profileData.profile
        && profileReducer.profileData.profile.email;

    if (userEmail && !isWebSettingsAllowed(userEmail)) {
        return <Redirect to={WEB} />;
    }

    return (
        <Fragment>
            <PageTitle title={t('enw-web-settings-page_title', 'Site Ayarları')} />
            <PageHeaderAtm
                exitText={t('btn-dashboard')}
                backButton={WEB}
                text={t('enw-web-settings-page_title', 'Site Ayarları')}
            />

            <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar(null)}
            >
                {snackbar ? (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
                        {snackbar.message}
                    </Alert>
                ) : <span />}
            </Snackbar>

            <Animated animationIn="fadeIn" animationInDuration={600} isVisible={true}>
                <Container maxWidth="xl">
                    <div className={`container-width pb-0 mb-24 ${classes.mobilePadding}`}>
                        {fetching && !hasWeb ? (
                            <div className={classes.loadingBox}>
                                <CircularProgress size={32} style={{color: '#d71f26'}} />
                            </div>
                        ) : !hasWeb ? (
                            <Alert severity="info" style={{marginTop: 24}}>
                                {t('enw-web-settings-no_web', 'Bu tesis için henüz bir web sitesi oluşturulmamış.')}
                            </Alert>
                        ) : (
                            <div className={classes.layout}>
                                <nav className={classes.rail} aria-label={t('enw-web-settings-nav_label', 'Ayar bölümleri')}>
                                    <ul className={classes.railList}>
                                        {SECTIONS.map((section) => (
                                            <li key={section.id}>
                                                <a
                                                    href={`#${section.id}`}
                                                    onClick={handleNavClick(section.id)}
                                                    className={`${classes.railLink} ${
                                                        activeSection === section.id ? classes.railLinkActive : ''
                                                    }`}
                                                >
                                                    <i className={section.icon} />
                                                    {t(section.label, section.fallback)}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>

                                <div className={classes.sections}>
                                    <HealthScoreCard
                                        editWebData={editContentReducer && editContentReducer.editWebData}
                                        webId={webId}
                                        extras={{
                                            blogCount: listCount(blogListData),
                                            popupCount: listCount(popupListData),
                                            faqCount: listCount(faqListData),
                                        }}
                                    />

                                    <PublishCard
                                        domain={domain}
                                        webReducer={webReducer}
                                        isPublish={isPublish}
                                        onPublish={handlePublish}
                                        onSuccessClose={handlePublishSuccessClose}
                                    />

                                    <SearchEngineCard
                                        domain={domain}
                                        activeComponents={thirdParty.activeComponents}
                                        googleStatus={{
                                            connected: !!(thirdParty.googleSiteVerification
                                                && thirdParty.googleSiteVerification.is_selected),
                                        }}
                                        notReady={notReady}
                                    />

                                    <SiteComponentsCard
                                        values={componentValues}
                                        onChange={handleComponentChange}
                                        googleRating={freshRating || googleRating}
                                        placeId={reviewPlaceId}
                                        showRating={reviewShowRating}
                                        onRefreshMap={handleRefreshMap}
                                        mapFetching={refreshState && refreshState.mapFetching}
                                        onRefreshReviews={handleRefreshReviews}
                                        reviewFetching={(refreshState && refreshState.reviewFetching) || googleRatingFetching}
                                        onSaveNumber={handleSaveNumber}
                                        numberDirty={numberDirty}
                                        settingsSaving={settingsSaving}
                                        notReady={notReady}
                                    />

                                    <ThemeCard
                                        currentTemplate={domain && domain.property_web_template}
                                        onSaved={() => setSnackbar({
                                            severity: 'success',
                                            message: t('enw-web-settings-theme_saved', 'Tema başarıyla güncellendi.'),
                                        })}
                                    />

                                    <InventoryGapsCard
                                        gaps={inventoryGaps}
                                        dayRange={INVENTORY_DAY_RANGE}
                                        fetching={inventoryFetching}
                                        hasData={inventoryHasData}
                                        inventoryUrl={`${NETWORK_INVENTORY_SHORT}/${BOOKING_ENGINE_CHANNEL_ID}`}
                                        windowStart={shiftDate(0)}
                                        windowEnd={shiftDate(INVENTORY_DAY_RANGE)}
                                    />

                                    <ImportCard />

                                    <GroupPageCard
                                        notReady={notReady}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </Animated>
            <style>{`
                .MuiCircularProgress-indeterminate {
                    color: #fff;
                    position: relative !important;
                }
                button.Mui-disabled {
                   color:rgba(0, 0, 0, 0.26)!important;
                }
                .MuiFormControl-root {
                    margin-top: 0px!important;
                }
            `}</style>
        </Fragment>
    );
}
